<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { DOCUMENT_TYPE, PAYMENT_TASK_STATUS, type DocumentType } from '@hsl-ems/contracts';

import { p1Api } from '@/lib/p1-api';
import { acceptedMime, sha256Hex } from '@/lib/p2-api';
import { p4Api, type PaymentTaskRecord } from '@/lib/p4-api';

const router = useRouter();
const tasks = ref<PaymentTaskRecord[]>([]);
const currentUserId = ref('');
const canProcess = ref(false);
const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const message = ref('');
const statusFilter = ref('OPEN');
const selectedId = ref('');
const payerAccountRef = ref('');
const paidAt = ref('');
const bankTransactionNo = ref('');
const failureReason = ref('');
const receiptFileId = ref('');
const receiptStatus = ref('');
const receiptName = ref('');

const typeLabels: Record<DocumentType, string> = {
  [DOCUMENT_TYPE.EXPENSE_REIMBURSEMENT]: '费用报销',
  [DOCUMENT_TYPE.LOAN]: '借款申请',
  [DOCUMENT_TYPE.CASH_REPAYMENT]: '现金还款',
  [DOCUMENT_TYPE.LOAN_OFFSET]: '借款冲销',
  [DOCUMENT_TYPE.CORPORATE_PAYMENT]: '对公付款',
};

const statusLabels: Record<string, string> = {
  PENDING: '待付款',
  PROCESSING: '处理中',
  SUCCEEDED: '已付款',
  FAILED: '付款失败',
  CANCELLED: '已取消',
  CORRECTED: '已更正',
};

const filteredTasks = computed(() =>
  statusFilter.value === 'ALL'
    ? tasks.value
    : tasks.value.filter((task) =>
        statusFilter.value === 'OPEN'
          ? ['PENDING', 'PROCESSING', 'FAILED'].includes(task.status)
          : task.status === statusFilter.value,
      ),
);
const selectedTask = computed(() => tasks.value.find((task) => task.id === selectedId.value));
const activeAttempt = computed(() =>
  selectedTask.value?.attempts.find((attempt) => attempt.status === 'PROCESSING'),
);
const canOperateAttempt = computed(
  () => canProcess.value && activeAttempt.value?.processorUserId === currentUserId.value,
);
const canStartAttempt = computed(
  () =>
    canProcess.value &&
    (selectedTask.value?.status === PAYMENT_TASK_STATUS.PENDING ||
      selectedTask.value?.status === PAYMENT_TASK_STATUS.FAILED),
);

onMounted(load);

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const me = await p1Api.me();
    if (me.mustChangePassword) return void (await router.replace('/change-password'));
    if (!me.permissions.includes('payment:read')) {
      error.value = '当前账号没有付款任务查看权限';
      return;
    }
    currentUserId.value = me.userId;
    canProcess.value = me.permissions.includes('payment:process');
    tasks.value = await p4Api.tasks();
    if (selectedId.value && !tasks.value.some((task) => task.id === selectedId.value))
      selectedId.value = '';
  } catch (reason) {
    const detail = reason instanceof Error ? reason.message : '付款任务加载失败';
    if (detail.includes('登录已失效')) return void (await router.replace('/login'));
    error.value = detail;
  } finally {
    loading.value = false;
  }
}

function selectTask(task: PaymentTaskRecord) {
  selectedId.value = task.id;
  error.value = '';
  message.value = '';
  receiptFileId.value = '';
  receiptStatus.value = '';
  receiptName.value = '';
  paidAt.value = localDateTime(new Date());
}

async function startAttempt() {
  const task = selectedTask.value;
  if (!task || !/^[A-Z][A-Z0-9_-]{1,63}$/.test(payerAccountRef.value)) {
    error.value = '请输入 2–64 位大写付款账户配置代码';
    return;
  }
  await runMutation(async () => {
    await p4Api.startAttempt(task.id, task.version, payerAccountRef.value);
    message.value = '付款处理已开始，请上传银行回单并登记结果';
  });
}

async function uploadReceipt(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  const attempt = activeAttempt.value;
  if (!file || !attempt) return;
  const mimeType = acceptedMime(file);
  if (!mimeType || !['application/pdf', 'image/jpeg', 'image/png'].includes(mimeType)) {
    error.value = '付款回单仅支持 PDF、JPG 或 PNG';
    return;
  }
  submitting.value = true;
  error.value = '';
  try {
    const intent = await p4Api.createReceiptUploadIntent({
      paymentAttemptId: attempt.id,
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
    });
    const uploaded = await fetch(intent.uploadUrl, {
      method: 'PUT',
      headers: intent.requiredHeaders,
      body: file,
    });
    if (!uploaded.ok) throw new Error('对象存储上传失败');
    await p4Api.completeUpload(intent.id, await sha256Hex(file));
    receiptFileId.value = intent.id;
    receiptName.value = file.name;
    receiptStatus.value = 'PENDING_SCAN';
    message.value = '回单已上传，正在进行安全扫描';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '付款回单上传失败';
  } finally {
    submitting.value = false;
  }
}

async function refreshReceipt() {
  if (!receiptFileId.value) return;
  try {
    const file = await p4Api.file(receiptFileId.value);
    receiptStatus.value = file.status;
    message.value =
      file.status === 'CLEAN' ? '回单安全扫描通过，可以登记付款成功' : '回单仍在扫描或未通过';
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '回单状态刷新失败';
  }
}

async function completeSuccess() {
  const task = selectedTask.value;
  const attempt = activeAttempt.value;
  if (!task || !attempt || !canOperateAttempt.value) return;
  if (
    receiptStatus.value !== 'CLEAN' ||
    !paidAt.value ||
    bankTransactionNo.value.trim().length < 2
  ) {
    error.value = '付款成功必须填写银行流水号、付款时间，并等待回单安全扫描通过';
    return;
  }
  await runMutation(async () => {
    await p4Api.completeAttempt(attempt.id, {
      taskVersion: task.version,
      attemptVersion: attempt.version,
      result: 'SUCCEEDED',
      paidAt: new Date(paidAt.value).toISOString(),
      bankTransactionNo: bankTransactionNo.value.trim(),
      receiptFileId: receiptFileId.value,
    });
    message.value = '付款成功已登记，原业务单据已完成';
    selectedId.value = '';
  });
}

async function completeFailure() {
  const task = selectedTask.value;
  const attempt = activeAttempt.value;
  if (!task || !attempt || !canOperateAttempt.value) return;
  if (failureReason.value.trim().length < 2) {
    error.value = '付款失败必须填写至少 2 个字的原因';
    return;
  }
  await runMutation(async () => {
    await p4Api.completeAttempt(attempt.id, {
      taskVersion: task.version,
      attemptVersion: attempt.version,
      result: 'FAILED',
      failureReason: failureReason.value.trim(),
    });
    message.value = '付款失败已登记，任务可重新发起付款';
    selectedId.value = '';
  });
}

async function runMutation(action: () => Promise<void>) {
  submitting.value = true;
  error.value = '';
  message.value = '';
  try {
    await action();
    await load();
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : '付款操作失败';
  } finally {
    submitting.value = false;
  }
}

function money(value: string) {
  return new Intl.NumberFormat('zh-CN', { style: 'currency', currency: 'CNY' }).format(
    Number(value),
  );
}

function dateTime(value: string | null) {
  if (!value) return '未设置';
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function localDateTime(value: Date) {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function snapshotName(snapshot: Record<string, unknown>) {
  return String(snapshot.name ?? snapshot.holderName ?? snapshot.accountName ?? '已冻结收款信息');
}
</script>

<template>
  <main class="workspace-shell payment-shell">
    <header class="workspace-header">
      <RouterLink class="workspace-brand" to="/documents">
        <span class="brand-mark" aria-hidden="true">郎</span>
        <span><strong>郎报通</strong><small>付款工作台</small></span>
      </RouterLink>
      <nav aria-label="主导航">
        <RouterLink to="/documents">我的单据</RouterLink>
        <RouterLink to="/approval-tasks">审批待办</RouterLink>
        <RouterLink class="active" to="/payment-tasks">付款处理</RouterLink>
        <RouterLink to="/payment-corrections">付款更正</RouterLink>
        <RouterLink to="/repayment-collections">还款确认</RouterLink>
        <RouterLink to="/loan-offsets">借款抵扣</RouterLink>
        <RouterLink to="/admin">组织与权限</RouterLink>
      </nav>
      <RouterLink class="text-link" to="/login">切换账号</RouterLink>
    </header>

    <section class="page-heading">
      <div>
        <p class="eyebrow">P4 · 付款与借款台账</p>
        <h1>付款工作台</h1>
        <p>仅显示授权经营组织内的付款任务；付款结果、银行回单和业务状态在同一事务内留痕。</p>
      </div>
      <button class="quiet-button" type="button" :disabled="loading" @click="load">刷新</button>
    </section>

    <div class="approval-tabs payment-tabs" role="tablist" aria-label="付款状态筛选">
      <button
        v-for="option in [
          ['OPEN', '待处理'],
          ['SUCCEEDED', '已付款'],
          ['ALL', '全部'],
        ]"
        :key="option[0]"
        type="button"
        role="tab"
        :aria-selected="statusFilter === option[0]"
        :class="{ active: statusFilter === option[0] }"
        @click="statusFilter = option[0]"
      >
        {{ option[1] }}
      </button>
    </div>

    <p v-if="error" class="inline-alert inline-alert--error" role="alert">{{ error }}</p>
    <p v-if="message" class="inline-alert" role="status">{{ message }}</p>
    <section v-if="loading" class="document-empty">正在加载付款任务…</section>
    <section v-else-if="!filteredTasks.length" class="document-empty">
      <strong>当前筛选条件下没有付款任务</strong>
      <p>单据完成最终审批且实付金额大于零后，会自动生成付款任务。</p>
    </section>
    <section v-else class="payment-layout">
      <div class="approval-list payment-list" aria-label="付款任务列表">
        <button
          v-for="task in filteredTasks"
          :key="task.id"
          type="button"
          class="approval-row payment-row"
          :class="{ selected: selectedId === task.id }"
          @click="selectTask(task)"
        >
          <span class="document-icon">付</span>
          <span class="document-main">
            <span class="document-title">
              <strong>{{ task.paymentNo }}</strong>
              <span class="status-pill" :data-status="task.status">{{
                statusLabels[task.status]
              }}</span>
            </span>
            <span
              >{{ task.sourceDocument.documentNo }} ·
              {{ typeLabels[task.sourceDocument.type] }}</span
            >
            <small
              >{{ snapshotName(task.payeeSnapshot) }} · {{ task.sourceDocument.description }}</small
            >
          </span>
          <strong class="document-amount">{{ money(task.amount) }}</strong>
        </button>
      </div>

      <aside v-if="selectedTask" class="form-section payment-operation-panel">
        <div class="section-heading-row">
          <div>
            <p class="eyebrow">{{ selectedTask.paymentNo }}</p>
            <h2>{{ statusLabels[selectedTask.status] }}</h2>
          </div>
          <button class="text-button" type="button" @click="selectedId = ''">关闭</button>
        </div>
        <dl class="payment-facts">
          <div>
            <dt>付款金额</dt>
            <dd>{{ money(selectedTask.amount) }}</dd>
          </div>
          <div>
            <dt>来源单据</dt>
            <dd>{{ selectedTask.sourceDocument.documentNo }}</dd>
          </div>
          <div>
            <dt>收款方</dt>
            <dd>{{ snapshotName(selectedTask.payeeSnapshot) }}</dd>
          </div>
          <div>
            <dt>期望付款日</dt>
            <dd>{{ dateTime(selectedTask.expectedPaymentDate) }}</dd>
          </div>
        </dl>

        <form v-if="canStartAttempt" class="payment-form" @submit.prevent="startAttempt">
          <label>
            <span>付款账户配置代码</span>
            <input
              v-model.trim="payerAccountRef"
              maxlength="64"
              placeholder="例如 HSL_CMB_MAIN"
              required
            />
            <small>填写系统配置代码，禁止填写真实银行卡号。</small>
          </label>
          <button class="primary-button" type="submit" :disabled="submitting">开始付款处理</button>
        </form>

        <div v-else-if="activeAttempt && canOperateAttempt" class="payment-form">
          <p class="section-hint">第 {{ activeAttempt.attemptNo }} 次付款，由当前账号处理中。</p>
          <label>
            <span>银行流水号</span>
            <input v-model.trim="bankTransactionNo" maxlength="128" />
          </label>
          <label>
            <span>实际付款时间</span>
            <input v-model="paidAt" type="datetime-local" />
          </label>
          <label class="upload-button payment-upload">
            {{ submitting ? '处理中…' : '上传银行回单' }}
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              :disabled="submitting"
              @change="uploadReceipt"
            />
          </label>
          <div v-if="receiptFileId" class="receipt-state">
            <span>{{ receiptName }} · {{ receiptStatus }}</span>
            <button class="text-button" type="button" @click="refreshReceipt">刷新扫描状态</button>
          </div>
          <button
            class="primary-button"
            type="button"
            :disabled="submitting"
            @click="completeSuccess"
          >
            登记付款成功
          </button>
          <div class="payment-failure-block">
            <label>
              <span>失败原因</span>
              <textarea v-model.trim="failureReason" maxlength="1000" rows="3" />
            </label>
            <button
              class="danger-button"
              type="button"
              :disabled="submitting"
              @click="completeFailure"
            >
              登记付款失败
            </button>
          </div>
        </div>
        <p v-else-if="activeAttempt" class="inline-alert">
          该任务正由 {{ activeAttempt.processor.name }} 处理，当前账号不可接管或登记结果。
        </p>
        <p
          v-else-if="
            !canProcess &&
            (selectedTask.status === PAYMENT_TASK_STATUS.PENDING ||
              selectedTask.status === PAYMENT_TASK_STATUS.FAILED)
          "
          class="inline-alert"
        >
          当前账号仅有付款查看权限，不可开始处理或登记结果。
        </p>

        <div v-if="selectedTask.attempts.length" class="payment-history">
          <h3>付款尝试记录</h3>
          <article v-for="attempt in selectedTask.attempts" :key="attempt.id">
            <strong
              >第 {{ attempt.attemptNo }} 次 ·
              {{ statusLabels[attempt.status] ?? attempt.status }}</strong
            >
            <small>{{ attempt.processor.name }} · {{ dateTime(attempt.startedAt) }}</small>
            <p v-if="attempt.failureReason">{{ attempt.failureReason }}</p>
            <p v-if="attempt.bankTransactionNo">流水号：{{ attempt.bankTransactionNo }}</p>
          </article>
        </div>
      </aside>
    </section>

    <nav class="mobile-nav" aria-label="移动端主导航">
      <RouterLink to="/documents">单据</RouterLink>
      <RouterLink to="/approval-tasks">审批</RouterLink>
      <RouterLink class="active" to="/payment-tasks">付款</RouterLink>
    </nav>
  </main>
</template>
